import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

/** `GET /home` (docs/dev/api-home.md): the start page in one response, scoped per user. */

export type HomeAreaKey = 'contacts' | 'properties' | 'deals' | 'calendar' | 'dialer' | 'controlling';
export type HomeAppointmentStatus = 'booked' | 'attended' | 'no_show' | 'cancelled';
export type HomeLifecycleGroup =
  'new' | 'call_booked' | 'follow_up' | 'prospect' | 'customer' | 'no_show' | 'deferred';

export interface HomeArea {
  key: HomeAreaKey;
  count: number | string;
  line: string;
  access: 'ok' | 'locked';
  href: string;
}

export interface HomeData {
  greeting: {
    firstName: string;
    organizationName: string;
    today: string;
    appointmentsToday: number;
    overdueTasks: number;
    newLeads7d: number;
  };
  kpis: {
    appointmentsToday: { count: number; next: { at: string; lastName: string } | null };
    openTasks: { count: number; overdue: number };
    newLeads: { count: number; deltaVsPreviousWeek: number };
    pipeline: { volumeCents: number | null; openDeals: number; reserved: number } | null;
  };
  areas: HomeArea[];
  pipeline: { name: string; stages: Array<{ key: string; label: string; count: number }> } | null;
  today: Array<{
    id: string;
    at: string;
    contactId: string | null;
    contactName: string;
    line: string;
    status: HomeAppointmentStatus;
  }>;
  contactsByLifecycle: Array<{ key: HomeLifecycleGroup; label: string; count: number }>;
}

export const homeKey = ['home'] as const;

export function useHome() {
  return useQuery({
    queryKey: homeKey,
    queryFn: ({ signal }) => api<HomeData>('/home', { signal }),
    staleTime: 30_000,
  });
}
